/**
 * DisputeNotice — shared dispute notice block.
 *
 * Renders one of two states:
 *   'contested'    — full red alert: identity dispute upheld by AP.
 *   'under-review' — amber notice: challenge filed, review in progress.
 *
 * Used by:
 *   - ProvenancePanel (in the authenticated vehicle dossier)
 *   - PublicDossierPage (in the public-facing provenance page)
 *
 * The crypto trust signals are NOT affected by a dispute — if the ledger
 * passes crypto it is intact. The dispute is a real-world identity claim,
 * not a record integrity failure.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No Web APIs without Capacitor equivalents.
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { ShieldAlert, ShieldX } from 'lucide-react';

export type DisputeState = 'none' | 'under-review' | 'contested';

export interface DisputeNoticeProps {
  disputeState: DisputeState;
}

export function DisputeNotice({ disputeState }: DisputeNoticeProps) {
  if (disputeState === 'none') return null;

  if (disputeState === 'contested') {
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          border: `1px solid ${T.red}`,
          borderLeft: `3px solid ${T.red}`,
          borderRadius: RADIUS.md,
          background: `color-mix(in srgb, ${T.red} 12%, transparent)`,
          padding: `${SPACE.sm}px ${SPACE.md}px`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: SPACE.sm,
        }}
      >
        <ShieldX
          size={16}
          color={T.red}
          aria-hidden="true"
          style={{ flexShrink: 0, marginTop: 2 }}
        />
        <div>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: T.red,
              margin: 0,
              fontFamily: F.body,
            }}
          >
            Identity disputed
          </p>
          <p
            style={{
              fontSize: 12,
              color: T.text,
              margin: `${SPACE.xs}px 0 0`,
              lineHeight: 1.5,
              fontFamily: F.body,
            }}
          >
            Absolute Pinks reviewed a challenge to this vehicle&rsquo;s stated identity and upheld
            the challenge: the claim of which car this is may not hold up. The sealed history is
            intact and untampered — what&rsquo;s in question is the real-world identity, not the
            record.
          </p>
        </div>
      </div>
    );
  }

  // 'under-review'
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.md,
        background: `color-mix(in srgb, ${T.border} 30%, transparent)`,
        padding: `${SPACE.sm}px ${SPACE.md}px`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: SPACE.sm,
      }}
    >
      <ShieldAlert
        size={14}
        color={T.textMuted}
        aria-hidden="true"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <div>
        <p
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: T.text,
            margin: 0,
            fontFamily: F.body,
          }}
        >
          Identity under review
        </p>
        <p
          style={{
            fontSize: 12,
            color: T.textMuted,
            margin: `${SPACE.xs}px 0 0`,
            lineHeight: 1.5,
            fontFamily: F.body,
          }}
        >
          Someone has questioned whether this is the vehicle it claims to be. Absolute Pinks is
          reviewing the challenge. The sealed history below is unaffected.
        </p>
      </div>
    </div>
  );
}
